import { SelectionModel } from '@angular/cdk/collections';
import { NgTemplateOutlet, AsyncPipe, DatePipe, NgFor, CommonModule } from '@angular/common';
import {
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  KeyValueDiffer,
  KeyValueDiffers,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  inject,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadge } from '@angular/material/badge';
import { MatIconButton } from '@angular/material/button';
import { MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import {
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelDescription,
} from '@angular/material/expansion';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatList, MatListItem, MatListItemIcon, MatListItemTitle, MatListItemMeta } from '@angular/material/list';
import { MatMenuTrigger, MatMenu, MatMenuItem, MatMenuContent } from '@angular/material/menu';
import { MatTabGroup, MatTab, MatTabLabel } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
import { ShareLinkDialogComponent } from '@app/taskpane/home/share-link-dialog/share-link-dialog.component';
import { SortCriteria } from '@app/taskpane/settings/sort-setting/sort-setting.component';
import {
  CaseReferenceDetails,
  CommonIP,
  CommonIPStateTabType,
  CommonIPStateTrifoldFiltersDialogData,
  CommonIPStateViewType,
  CommonNote,
  DisplayableFieldResponse,
  FileVersionDO,
  ImageResponse,
  IP,
  ReferenceDetails,
  TrifoldDetailMap,
  WorkflowDO,
  WsFile,
} from '@model/ftf-api.model';
import { FirstToFileFeatureFlags } from '@model/widget.model';
import { AccountService } from '@service/account.service';
import { ApiService } from '@service/api.service';
import { DataTransformService } from '@service/data-transform.service';
import { PdfPreviewService } from '@service/pdf-preview.service';
import { SettingsService } from '@service/settings.service';
import { TrifoldSortPipe } from '@shared/components/ip-trifold/ip-trifold-sort-pipe';
import { formatDateYYYYMMDD, getTrifoldDocIcon } from '@utils/file-utils';
import {
  BehaviorSubject,
  debounceTime,
  distinct,
  filter,
  forkJoin,
  from,
  map,
  mergeMap,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  toArray,
} from 'rxjs';

import { IpDetailComponent } from '../ip-detail/ip-detail.component';
import { IpIconComponent } from '../ip-icon/ip-icon.component';
import { SearchableComponent } from '../searchable/searchable.component';
import { WorkflowFileDetail, WorkflowDetailComponent } from '../workflow-detail/workflow-detail.component';
import {
  DateFilterType,
  DEFAULT_TRIFOLD_FILTER_STATE,
  TrifoldFilterDialogComponent,
  TrifoldFilterDialogFormData,
} from './trifold-filter-dialog/trifold-filter-dialog.component';

export interface TrifoldItemEvent {
  item: CommonIP;
}
export type TrifoldItemTabEvent = TrifoldItemEvent & { tabIndex: number; view: CommonIPStateViewType };
export type TrifoldItemUploadEvent = TrifoldItemEvent & { files?: File[]; trifold: IP.TrifoldEnum };
export type TrifoldItemFilterEvent = TrifoldItemEvent & { filters: CommonIPStateTrifoldFiltersDialogData };
export interface TrifoldItemWorkflowEvent {
  item: CommonIP;
  workflow: WorkflowDO;
}
export type TrifoldItemFileEvent = TrifoldItemEvent & { type: 'reference' | 'trifold'; doc: WsFile | CaseReferenceDetails };
export type TrifoldItemFileVersionEvent = TrifoldItemFileEvent & { trifold: IP.TrifoldEnum; version: FileVersionDO[] };
export type TrifoldItemSelectionEvent = TrifoldItemEvent & { selection: SelectionDocument[] };

type OverlayType = 'docDetails' | 'refDetails';
type OverlayDetail = { title: string; type: OverlayType } & DisplayableFieldResponse;
type FilteredTrifoldFiles = { [key in IP.TrifoldEnum]: WsFile[] };
export type SelectionType = 'trifold' | 'reference';

/**
 * Represents a document selected in the trifold view.
 */
export interface SelectionDocument {
  type: SelectionType;
  doc: WsFile | CaseReferenceDetails;
}

/**
 * Tracks the item by a specific key.
 * @param key The key to track by.
 * @returns A function that returns the value of the key for the item.
 */
export function trackByKey<T, P>(key: keyof T) {
  return (index: number, item: T): P => item[key] as P;
}

/**
 * Compares two filter states for equality.
 * @param a The first filter state.
 * @param b The second filter state.
 * @returns True if the filter states are equal, false otherwise.
 */
export function areFiltersEqual(a: CommonIPStateTrifoldFiltersDialogData, b: CommonIPStateTrifoldFiltersDialogData): boolean {
  const aSorted = [...(a?.docSubtypeId ?? [])].sort();
  const bSorted = [...(b?.docSubtypeId ?? [])].sort();
  return JSON.stringify(aSorted) === JSON.stringify(bSorted);
}

@Component({
  selector: 'ip-trifold',
  templateUrl: './ip-trifold.component.html',
  styleUrls: ['./ip-trifold.component.scss'],
  imports: [
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatTooltip,
    MatIcon,
    MatExpansionPanelDescription,
    IpDetailComponent,
    IpIconComponent,
    NgFor,
    CommonModule,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatTabGroup,
    MatTab,
    NgTemplateOutlet,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatIconButton,
    MatSuffix,
    MatBadge,
    SearchableComponent,
    MatList,
    MatListItem,
    MatCheckbox,
    MatListItemIcon,
    MatListItemTitle,
    MatListItemMeta,
    MatMenuContent,
    WorkflowDetailComponent,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardSubtitle,
    MatCardContent,
    MatTabLabel,
    AsyncPipe,
    DatePipe,
    TrifoldSortPipe,
  ],
})
export class IpTrifoldComponent implements OnInit, OnDestroy, DoCheck {
  private accountService = inject(AccountService);
  private dataTransformService = inject(DataTransformService);
  private apiService = inject(ApiService);
  private settingsService = inject(SettingsService);
  private dialogService = inject(MatDialog);
  private pdfPreviewService = inject(PdfPreviewService);
  private differs = inject(KeyValueDiffers);
  elementRef = inject(ElementRef);

  // Public instance properties
  @Input({ required: true }) item!: CommonIP;
  @Input() attachActions = false;
  @Input() widgetProductName: string | null = null;
  @Input() orientation: 'Vertical' | 'Horizontal' = 'Vertical';
  @Input() uploadIncludeFileBrowse = false;
  @Input() canUploadNewVersion: ((doc: WsFile) => boolean) | null = null;

  @Output() refresh = new EventEmitter<TrifoldItemEvent>();
  @Output() upload = new EventEmitter<TrifoldItemUploadEvent>();
  @Output() uploadVersion = new EventEmitter<TrifoldItemFileEvent>();
  @Output() download = new EventEmitter<TrifoldItemFileEvent>();
  @Output() pdfPreview = new EventEmitter<TrifoldItemFileEvent>();
  @Output() openFTF = new EventEmitter<TrifoldItemEvent>();
  @Output() openIPMS = new EventEmitter<TrifoldItemEvent>();
  @Output() closeTrifold = new EventEmitter<TrifoldItemEvent>();
  @Output() favorite = new EventEmitter<TrifoldItemEvent>();
  @Output() workflow = new EventEmitter<TrifoldItemWorkflowEvent>();
  @Output() attach = new EventEmitter<TrifoldItemFileEvent>();
  @Output() widget = new EventEmitter<TrifoldItemSelectionEvent>();
  @Output() view = new EventEmitter<TrifoldItemTabEvent>();
  @Output() tab = new EventEmitter<TrifoldItemTabEvent>();
  @Output() expansion = new EventEmitter<TrifoldItemEvent>();
  @Output() version = new EventEmitter<TrifoldItemFileVersionEvent>();
  @Output() undoCheckout = new EventEmitter<TrifoldItemFileEvent>();
  @Output() unlock = new EventEmitter<TrifoldItemFileEvent>();
  @Output() checkout = new EventEmitter<TrifoldItemFileEvent>();
  @Output() lock = new EventEmitter<TrifoldItemFileEvent>();
  @Output() updateFilters = new EventEmitter<TrifoldItemFilterEvent>();
  @Output() deleteFile = new EventEmitter<TrifoldItemFileEvent>();
  @Output() familyCaseClick = new EventEmitter<CommonIP>();

  @ViewChildren(SearchableComponent) searchableComponents!: QueryList<SearchableComponent<unknown>>;

  isMac = false;
  trifoldMap: TrifoldDetailMap;
  trifoldOrder: IP.TrifoldEnum[] = ['L', 'M', 'R'];
  trademarkThumbnail: ImageResponse | null = null;
  overlayDetail: OverlayDetail | null = null;
  selectionModel: SelectionModel<SelectionDocument>;
  selectionMode: boolean = false;
  canUploadAnyTrifolds: boolean = false;
  canAccessAnyTrifolds: boolean = false;
  tooltipDelay = 500;
  tabAnimationDuration = '0ms';
  searchControl = new FormControl<string>('', { nonNullable: true });
  filteredDocs$: Observable<FilteredTrifoldFiles>;
  isFilterVisible = false;
  detailTabs!: CommonIPStateTabType[];
  familyCaseItems: CommonIP[] = [];
  familyCasesLoaded = false;
  totalFamilyCaseCount = 0;
  familyCasePageIndex = 0;
  readonly FAMILY_CASE_LIMIT = 10;
  private allFamilyCaseItems: CommonIP[] = [];

  // Private instance properties
  private readonly $ngDestroyed = new Subject<void>();
  private isToggleKeyPressed = false;
  private isMouseOver = false;
  private fetchDocData$ = new Subject<{ id: number; trifold: string }>();
  private fetchDocDataReset$ = new Subject<void>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private itemDiffer: KeyValueDiffer<string, any>;
  private filterSubject = new BehaviorSubject<TrifoldFilterDialogFormData>(DEFAULT_TRIFOLD_FILTER_STATE);
  private patentSortSettings: SortCriteria[] = [];
  private trademarkSortSettings: SortCriteria[] = [];
  private matterSortSettings: SortCriteria[] = [];

  /**
   * Gets whether the item is expanded.
   * @type {boolean}
   */
  get expanded() {
    return this.item.state?.expanded ?? false;
  }

  /**
   * Gets whether the item is expanded.
   * @type {boolean}
   */
  set expanded(value: boolean) {
    if (this.expanded === value) return;
    this.expansion.emit({ item: this.item });
  }

  /**
   * Gets or sets the selected detail tab index.
   * @type {number}
   */
  get selectedDetailIndex() {
    return this.item.state?.detailTabIndex ?? -1;
  }

  /**
   * Sets the selected detail tab index.
   * @type {number}
   */
  set selectedDetailIndex(value: number) {
    if (this.selectedDetailIndex === value) return;
    this.tab.emit({ item: this.item, tabIndex: value, view: 'detail' });
  }

  /**
   * Gets the selected trifold tab index.
   * @type {number}
   */
  get selectedTrifoldIndex() {
    return this.item.state?.trifoldTabIndex ?? -1;
  }

  /**
   * Set the selected trifold tab index.
   * @type {number}
   */
  set selectedTrifoldIndex(value: number) {
    if (this.selectedTrifoldIndex === value) return;
    this.tab.emit({ item: this.item, tabIndex: value, view: 'trifold' });
  }

  /**
   * Gets the current view.
   * @type {*}
   */
  get currentView() {
    return this.item.state?.view;
  }

  /**
   * Sets the current view.
   *
   * @type {*}
   */
  set currentView(value: CommonIPStateViewType | undefined) {
    if (this.currentView === value) return;
    const tabIndex = value === 'detail' ? this.selectedDetailIndex : this.selectedTrifoldIndex;
    this.view.emit({ item: this.item, tabIndex, view: value ?? 'trifold' });
  }

  /**
   * Gets the settings service.
   * @readonly
   * @type {*}
   */
  get settings() {
    return this.settingsService;
  }

  /**
   * Gets the trifold filters.
   *
   * @type {CommonIPStateTrifoldFiltersDialogData}
   */
  get trifoldFilters(): CommonIPStateTrifoldFiltersDialogData {
    return this.item.state!.trifoldFilters!;
  }

  /**
   * Sets the trifold filters.
   * @type {CommonIPStateTrifoldFiltersDialogData}
   */
  set trifoldFilters(value: CommonIPStateTrifoldFiltersDialogData) {
    // if the value is the same, do not emit
    if (this.item && areFiltersEqual(this.trifoldFilters, value)) return;
    // update the item state with the new filters
    this.updateFilters.emit({ item: this.item, filters: value });
  }

  /**
   * Creates an instance of IpTrifoldComponent.
   *
   * @remarks
   * The constructor initializes the trifold map, detail tabs, and sets up observables for filtering documents.
   * It also sets up a differ to track changes to the item and a selection model for document selection.
   */
  constructor() {
    this.trifoldMap = this.accountService.trifoldMap;
    this.initDetailTabs();
    this.setupDocVersionFetch();

    this.filteredDocs$ = this.filterSubject.pipe(
      debounceTime(300),
      switchMap((criteria) => this.getFilteredTrifoldDocs(criteria))
    );
    this.filterSubject.subscribe((criteria) => {
      this.searchControl.setValue(criteria.search, { emitEvent: false });
      // emit the filter event with the current item and criteria
      this.trifoldFilters = { docSubtypeId: criteria.type };
    });
    this.searchControl.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
      this.filterSubject.next({ ...this.filterSubject.value, search: value });
    });
    this.itemDiffer = this.differs.find({}).create();
    this.selectionModel = new SelectionModel<SelectionDocument>(
      true,
      [],
      true,
      (a, b) => a.type === b.type && a.doc.fileId === b.doc.fileId
    );
  }

  /**
   * Gets the sort criteria.
   *
   * @readonly
   * @type {SortCriteria[]}
   */
  get sortCriteria(): SortCriteria[] {
    switch (this.item.ipType) {
      case 'Trademark':
        return this.trademarkSortSettings;
      case 'Matter':
        return this.matterSortSettings;
      case 'Patent':
      default:
        return this.patentSortSettings;
    }
  }

  /**
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   *
   * @remarks
   * This method initializes user preferences and permissions, sets up detail tabs,
   * determines the default state of the component, applies trifold filters, and sets up
   * a differ to track changes to the item. It also checks if the platform is Mac.
   */
  ngOnInit() {
    this.accountService.isAccountDetailsLoaded$.pipe(takeUntil(this.$ngDestroyed)).subscribe((loaded) => {
      if (!loaded) return;
      this.trifoldMap = this.accountService.trifoldMap;
      this.trifoldOrder = this.accountService.accessibleTrifolds[this.item.ipType];
      this.canUploadAnyTrifolds = this.accountService.uploadableTrifolds[this.item.ipType].length > 0;
      this.canAccessAnyTrifolds = this.trifoldOrder.length > 0;
      this.initDetailTabs();
    });
    this.patentSortSettings = this.settingsService.getPatentSortSettings();
    this.trademarkSortSettings = this.settingsService.getTrademarkSortSettings();
    this.matterSortSettings = this.settingsService.getMatterSortSettings();
    this.initItem();
    this.determineDefaultState();
    this.applyTrifoldFilters();
    this.itemDiffer.diff(this.item);
    this.isMac = navigator.platform?.toUpperCase().indexOf('MAC') >= 0;
  }

  /**
   * Initializes the detail tabs based on user roles and item type.
   *
   * @remarks
   * This method sets up the detail tabs to be displayed in the component.
   * It includes 'biblio' and 'notes' tabs by default, and conditionally adds
   * 'references' and 'workflow' tabs based on user roles and the type of item.
   */
  initDetailTabs() {
    const detailTabs: CommonIPStateTabType[] = ['biblio', 'notes'];
    if (this.accountService.hasRole('ROLE_REFERENCE_ACCESS') && this.item?.ipType === 'Patent') {
      detailTabs.push('references');
    }
    if (this.accountService.hasRole('ROLE_WORKFLOW_ACCESS')) {
      detailTabs.push('workflow');
    }
    this.detailTabs = detailTabs;
  }

  ngDoCheck() {
    const changes = this.itemDiffer.diff(this.item);
    if (changes) {
      this.applyTrifoldFilters();
      if (this.searchableComponents) {
        this.searchableComponents.forEach((comp) => comp.refresh());
      }
      this.fetchDocDataReset$.next();
      this.reviewSelectionModel();
      this.familyCasesLoaded = false;
      this.familyCaseItems = [];
    }
  }

  /**
   * Lifecycle hook that is called when the directive is destroyed.
   *
   * @remarks
   * This method cleans up subscriptions and completes the destroyed subject
   * to prevent memory leaks when the component is destroyed.
   */
  ngOnDestroy() {
    this.$ngDestroyed.next();
    this.$ngDestroyed.complete();
  }

  /**
   * Opens the share link dialog for the specified document.
   * @param doc The document to share.
   */
  onOpenShareLinkDialog(doc: WsFile) {
    this.dialogService.open(ShareLinkDialogComponent, {
      data: { fileId: doc.fileId!, identifier: this.item.identifier, ipType: this.item.ipType },
      width: '250px',
    });
  }

  /**
   * Reviews the selection model and clears it if not in selection mode.
   * @returns void
   */
  reviewSelectionModel() {
    if (!this.selectionMode) {
      this.selectionModel.clear();
      return;
    }

    // ensure that the selection model has items selected that are in the data
    const rec = this.item.trifold;
    if (rec) {
      this.selectionModel.selected.forEach((selected) => {
        const found =
          selected.type === 'reference'
            ? this.item.detail?.references?.some((ref) => ref.fileId === selected.doc.fileId)
            : rec.L.some((doc) => doc.fileId === selected.doc.fileId) ||
            rec.M.some((doc) => doc.fileId === selected.doc.fileId) ||
            rec.R.some((doc) => doc.fileId === selected.doc.fileId);
        if (!found) {
          this.selectionModel.deselect(selected);
        }
      });
    }
  }

  /**
   * Sets up the document version fetch mechanism.
   *
   * @remarks
   * This method initializes an observable pipeline that listens for document hover events.
   * When a document is hovered over, it fetches the latest version information for that document
   * after a debounce period. The fetched version data is then emitted through the `version` event emitter.
   */
  setupDocVersionFetch() {
    this.fetchDocData$
      .pipe(
        takeUntil(this.$ngDestroyed),
        debounceTime(this.tooltipDelay),
        distinct(({ id, trifold }) => `${id}-${trifold}`, this.fetchDocDataReset$),
        mergeMap(({ id, trifold }) => this.apiService.getVersion(id, this.item.ipType).pipe(map((version) => ({ id, trifold, version }))))
      )
      .subscribe((res) => {
        // update the version data for the document
        const doc = (this.item.trifold?.[res.trifold as IP.TrifoldEnum] ?? []).find((doc) => doc.fileId === res.id);
        if (doc) {
          this.version.emit({
            item: this.item,
            type: 'trifold',
            doc: doc,
            trifold: res.trifold as IP.TrifoldEnum,
            version: res.version ?? [],
          });
        }
      });
  }

  /**
   * Handles the hover event for a trifold document.
   * @param doc The document being hovered over.
   * @param trifold The trifold type.
   * @returns void
   */
  onTrifoldDocHover(doc: WsFile, trifold: string) {
    // bail out of doc has no id or versions are already set
    if (!doc.fileId || !!doc.versions) return;
    this.fetchDocData$.next({ id: doc.fileId, trifold });
  }

  /**
   * Initialize the item by loading any extra data needed to
   * suppliment widget beyond what is provided by CommonIP item.
   */
  private initItem() {
    // load trademark image in
    if (this.item.ipType === 'Trademark') {
      this.apiService.findTrademarkThumbnail(this.item.identifier).subscribe((image) => {
        this.trademarkThumbnail = image;
      });
    }
    this.overlayDetail = null;
  }

  /**
   * Handles the item event.
   * @param event The mouse event.
   * @param emitter The event emitter.
   */
  onItemEvent(event: MouseEvent | undefined, emitter: EventEmitter<TrifoldItemEvent>) {
    if (event) event.stopPropagation();
    emitter.emit({ item: this.item });
  }

  private loadFamilyCases(): void {
    if (this.familyCasesLoaded) {
      return;
    }

    const { identifier, ipType } = this.item;

    switch (ipType) {
      case IP.TypeEnum.Patent:
        this.apiService.findCaseFamilyAssociations(identifier).subscribe({
          next: (familyAssociations) => {
            this.allFamilyCaseItems = familyAssociations.map((assoc) => ({
              identifier: assoc.primaryCaseNm!,
              ipType: IP.TypeEnum.Patent,
            } as CommonIP));
            this.totalFamilyCaseCount = this.allFamilyCaseItems.length;
            this.familyCasePageIndex = 0;
            this.updateFamilyCasePage();
            this.familyCasesLoaded = true;
          },
          error: () => {
            this.allFamilyCaseItems = [];
            this.familyCaseItems = [];
            this.totalFamilyCaseCount = 0;
            this.familyCasesLoaded = true;
          },
        });
        break;
      case IP.TypeEnum.Trademark:
        this.apiService.findTrademarkFamilyAssociations(identifier).subscribe({
          next: (familyAssociations) => {
            this.allFamilyCaseItems = familyAssociations.map((assoc) => ({
              identifier: assoc.companyCaseNm!,
              ipType: IP.TypeEnum.Trademark,
            } as CommonIP));
            this.totalFamilyCaseCount = this.allFamilyCaseItems.length;
            this.familyCasePageIndex = 0;
            this.updateFamilyCasePage();
            this.familyCasesLoaded = true;
          },
          error: () => {
            this.allFamilyCaseItems = [];
            this.familyCaseItems = [];
            this.totalFamilyCaseCount = 0;
            this.familyCasesLoaded = true;
          },
        });
        break;
      case IP.TypeEnum.Matter:
        this.apiService.findMatterFamilyAssociations(identifier).subscribe({
          next: (familyAssociations) => {
            this.allFamilyCaseItems = familyAssociations.map((assoc) => ({
              identifier: assoc.matterNm!,
              ipType: IP.TypeEnum.Matter,
            } as CommonIP));
            this.totalFamilyCaseCount = this.allFamilyCaseItems.length;
            this.familyCasePageIndex = 0;
            this.updateFamilyCasePage();
            this.familyCasesLoaded = true;
          },
          error: () => {
            this.allFamilyCaseItems = [];
            this.familyCaseItems = [];
            this.totalFamilyCaseCount = 0;
            this.familyCasesLoaded = true;
          },
        });
        break;
      default:
        this.allFamilyCaseItems = [];
        this.familyCaseItems = [];
        this.totalFamilyCaseCount = 0;
        this.familyCasesLoaded = true;
        break;
    }
  }

  onFamilyCasesMenuOpened(): void {
    this.loadFamilyCases();
  }

  onFamilyCaseClick(familyCase: CommonIP): void {
    this.familyCaseClick.emit(familyCase);
  }

  get familyCaseStartIndex(): number {
    return this.familyCasePageIndex * this.FAMILY_CASE_LIMIT;
  }

  get familyCaseEndIndex(): number {
    return Math.min(this.familyCaseStartIndex + this.FAMILY_CASE_LIMIT, this.totalFamilyCaseCount);
  }

  get canGoPreviousFamilyCase(): boolean {
    return this.familyCasePageIndex > 0;
  }

  get canGoNextFamilyCase(): boolean {
    return this.familyCaseEndIndex < this.totalFamilyCaseCount;
  }

  private updateFamilyCasePage(): void {
    const start = this.familyCasePageIndex * this.FAMILY_CASE_LIMIT;
    const end = start + this.FAMILY_CASE_LIMIT;
    this.familyCaseItems = this.allFamilyCaseItems.slice(start, end);
  }

  previousFamilyCasePage(event: MouseEvent): void {
    event.stopPropagation();
    if (this.canGoPreviousFamilyCase) {
      this.familyCasePageIndex--;
      this.updateFamilyCasePage();
    }
  }

  nextFamilyCasePage(event: MouseEvent): void {
    event.stopPropagation();
    if (this.canGoNextFamilyCase) {
      this.familyCasePageIndex++;
      this.updateFamilyCasePage();
    }
  }

  onItemUploadEvent(event: MouseEvent) {
    event.stopPropagation();
    this.upload.emit({ item: this.item, trifold: this.uploadTrifold });
  }

  /**
   * Handles the item workflow event.
   * @param workflow The workflow data.
   */
  onItemWorkflowEvent(workflow: WorkflowDO) {
    this.workflow.emit({ item: this.item, workflow });
  }

  /**
   * Determines the default state of the component based on action and item properties.
   * @param actionType The action type.
   * @param overlayType The overlay type.
   * @param docType The document type.
   * @param doc The document.
   * @returns The action handler function.
   */
  private getActionHandler(
    actionType: 'icon' | 'text',
    overlayType: OverlayType,
    docType: 'reference' | 'trifold',
    doc: WsFile | ReferenceDetails
  ) {
    const actionSetting =
      actionType === 'icon'
        ? this.settingsService.getSettings().trifoldDocumentIconAction
        : this.settingsService.getSettings().trifoldDocumentTextAction;

    switch (actionSetting) {
      case '1':
        return (event: MouseEvent | null = null) => this.onOverlayDetailsOpen(event, overlayType, doc);
      case '2':
        return (event: MouseEvent | null = null) => this.onItemFilePreviewEvent(event, docType, doc, this.pdfPreview);
      case '3':
        return (event: MouseEvent | null = null) => this.onItemFileEvent(event, docType, doc, this.download);
      default:
        return (event: MouseEvent | null = null) => this.onOverlayDetailsOpen(event, overlayType, doc);
    }
  }

  /**
   * Gets the workflow action for a document.
   * @param doc The document.
   * @param actionType The action type.
   * @returns The action handler function.
   */
  getWorkflowAction(doc: WorkflowFileDetail, actionType: 'icon' | 'text') {
    // workflow documents can be either a reference or a trifold document
    const overlayType: OverlayType = doc.type === 'Reference' ? 'refDetails' : 'docDetails';
    const docType = doc.type === 'Reference' ? 'reference' : 'trifold';
    return this.getActionHandler(actionType, overlayType, docType, doc.raw);
  }

  /**
   * Gets the document action for a document.
   * @param doc The document.
   * @param actionType The action type.
   * @param overlayType The overlay type.
   * @param docType The document type.
   * @returns The action handler function.
   */
  getDocumentAction(
    doc: WsFile | ReferenceDetails,
    actionType: 'icon' | 'text',
    overlayType: OverlayType = 'docDetails',
    docType: 'reference' | 'trifold' = 'trifold'
  ) {
    return this.getActionHandler(actionType, overlayType, docType, doc);
  }

  /**
   * Gets the file event for a document.
   * @param event The mouse event.
   * @param type The document type.
   * @param doc The document.
   * @param emitter The event emitter.
   * @param stopPropagation Whether to stop event propagation.
   * @returns The action handler function.
   */
  onItemFileEvent(
    event: MouseEvent | null,
    type: 'reference' | 'trifold',
    doc: WsFile | CaseReferenceDetails,
    emitter: EventEmitter<TrifoldItemFileEvent>,
    stopPropagation = true
  ) {
    if (stopPropagation && event) event.stopPropagation();
    if (!this.isFeatureEnabled('documentDownload')) return;
    emitter.emit({ item: this.item, type, doc });
  }

  /**
   * Handles the item file event.
   * @param event The mouse event.
   * @param type The document type.
   * @param doc The document.
   * @param emitter The event emitter.
   * @param stopPropagation Whether to stop event propagation.
   * @returns The action handler function.
   */
  onItemFilePreviewEvent(
    event: MouseEvent | null,
    type: 'reference' | 'trifold',
    doc: WsFile | CaseReferenceDetails,
    emitter: EventEmitter<TrifoldItemFileEvent>,
    stopPropagation = true
  ) {
    if (stopPropagation && event) event.stopPropagation();
    if (!this.isFeatureEnabled('documentPreview')) return;
    this.pdfPreviewService.previewByFileId(doc.fileId!);
  }

  /**
   * Handles the item selection event.
   * @param event The mouse event.
   * @param selection The selected documents.
   * @param emitter The event emitter.
   * @param stopPropagation Whether to stop event propagation.
   */
  onItemSelectionEvent(
    event: MouseEvent,
    selection: SelectionDocument[],
    emitter: EventEmitter<TrifoldItemSelectionEvent>,
    stopPropagation = true
  ) {
    if (stopPropagation && event) event.stopPropagation();
    emitter.emit({ item: this.item, selection });
  }

  /**
   * Handles the toggle IP details event.
   * @param event The mouse event.
   * @param item The IP item.
   */
  onToggleIpDetails(event: MouseEvent, item: CommonIP) {
    event.stopPropagation();
    const view = item.state?.view === 'detail' ? 'trifold' : 'detail';
    const tabIndex = view === 'detail' ? this.selectedDetailIndex : this.selectedTrifoldIndex;
    this.view.emit({ item, tabIndex, view });
  }

  /**
   * Handles the toggle filter event.
   * @param event The mouse event.
   */
  onToggleFilter(event: MouseEvent) {
    event.stopPropagation();
    this.isFilterVisible = !this.isFilterVisible;
  }

  /**
   * Handles the clear filters event.
   * @param event The mouse event.
   */
  onClearFilters(event: MouseEvent) {
    event.stopPropagation();
    this.clearTrifoldFilters();
  }

  /**
   * Handles the more filters click event.
   * @param event The mouse event.
   */
  onMoreFiltersClick(event: MouseEvent) {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    const top = target.getBoundingClientRect().bottom;
    const ref = this.dialogService.open(TrifoldFilterDialogComponent, {
      data: { item: this.item, oldValue: this.filterSubject.value },
      width: '300px',
      maxWidth: '85vw',
    });
    ref.updatePosition({ top: `${top}px` });
    ref.afterClosed().subscribe((formData) => {
      if (formData) {
        if (formData.clear) {
          this.clearTrifoldFilters();
        } else {
          this.filterSubject.next(formData.value);
        }
      }
    });
  }

  /**
   * Handles the overlay details open event.
   * @param event The mouse event.
   * @param type The overlay type.
   * @param data The document or reference data.
   * @param stopPropagation Whether to stop event propagation.
   */
  onOverlayDetailsOpen(event: MouseEvent | null, type: OverlayType, data: WsFile | CaseReferenceDetails, stopPropagation = true) {
    if (stopPropagation && event) event.stopPropagation();
    switch (type) {
      case 'docDetails':
        this.overlayDetail = {
          title: 'Document Details',
          type,
          ...this.dataTransformService.displayableFileDetails(data),
        };
        break;
      case 'refDetails':
        this.overlayDetail = {
          title: 'Reference Details',
          type,
          ...this.dataTransformService.displayableRefDetails(data),
        };
        break;
      default:
        this.overlayDetail = null;
        break;
    }
  }

  /**
   * Handles the overlay details close event.
   * @returns void
   */
  onOverlayDetailsClose() {
    this.overlayDetail = null;
  }

  /**
   * Handles the file browsing event.
   * @param event The file input change event.
   * @returns
   */
  onBrowseFiles(event: Event) {
    const target = event.target as HTMLInputElement;
    if (!target.files) return;
    const files = Array.from(target.files);
    target.value = '';
    // have to delay it otherwise file api will keep popup from opening
    setTimeout(() => this.upload.emit({ item: this.item, files, trifold: this.uploadTrifold }), 100);
  }

  /**
   * Handle the keyboard and mouse events to determine if the user is in selection mode.
   * @param event
   */
  @HostListener('document:keydown', ['$event'])
  @HostListener('document:keyup', ['$event'])
  @HostListener('mouseenter', ['$event'])
  @HostListener('mouseleave', ['$event'])
  onHandleSelectionToggleEvent(event: KeyboardEvent | MouseEvent) {
    if (event instanceof KeyboardEvent) {
      if (this.isMac) {
        this.isToggleKeyPressed = event.type === 'keydown' && event.metaKey;
      } else {
        this.isToggleKeyPressed = event.type === 'keydown' && event.ctrlKey;
      }
    } else if (event instanceof MouseEvent) {
      this.isMouseOver = event.type === 'mouseenter';
    }

    if (this.isToggleKeyPressed && this.isMouseOver) {
      this.selectionMode = true;
    } else if ((!this.isToggleKeyPressed || !this.isMouseOver) && this.selectionModel.isEmpty()) {
      this.selectionMode = false;
    }
  }

  /**
   * Clears the selection.
   *
   */
  onClearSelection() {
    this.selectionModel.clear();
    this.selectionMode = false;
  }

  /**
   * Applies the trifold filters to the documents.
   */
  clearTrifoldFilters() {
    this.filterSubject.next(DEFAULT_TRIFOLD_FILTER_STATE);
    this.isFilterVisible = false;
    this.trifoldFilters = { docSubtypeId: [] };
  }

  /**
   * Retrieves the filtered trifold documents based on the specified criteria.
   * @param criteria The filtering criteria.
   * @returns An observable containing the filtered trifold documents.
   */
  getFilteredTrifoldDocs(criteria: TrifoldFilterDialogFormData): Observable<FilteredTrifoldFiles> {
    if (!this.item?.trifold) {
      return of({ L: [], M: [], R: [] });
    }

    const searchLower = criteria.search.trim().toLowerCase();

    let ddub = null;
    let ddlb = null;
    if (criteria.date) {
      // calculate upperbound for the day
      ddub = new Date(criteria.date);
      ddub.setHours(23, 59, 59, 999);
      // calculate lowerbound for the day
      ddlb = new Date(criteria.date);
      ddlb.setHours(0, 0, 0, 0);
    }
    const { isFilterSet } = this;
    const trifolds: IP.TrifoldEnum[] = ['L', 'M', 'R'];

    const obs = trifolds.map((trifold) =>
      from(this.item.trifold![trifold]).pipe(
        filter((doc) => {
          // if export controlled, do not show
          if (this.isExportControlled(doc)) return false;

          // if nothing to filter allow doc.. no need to check further
          if (!isFilterSet) return true;

          let resp =
            searchLower === '' ||
            (doc.title ?? '').toLowerCase().includes(searchLower) ||
            (doc.fileName ?? '').toLowerCase().includes(searchLower) ||
            (doc.docSubtype ?? '').toLowerCase().includes(searchLower);
          if (criteria.date) {
            const { date } = doc;
            if (!date) return false;
            if (criteria.dateFilterType === DateFilterType.Equal) {
              resp &&= date && date.getTime() >= ddlb!.getTime() && date.getTime() <= ddub!.getTime();
            } else if (criteria.dateFilterType === DateFilterType.GreaterEq) {
              resp &&= date && date.getTime() > ddlb!.getTime();
            } else if (criteria.dateFilterType === DateFilterType.LessEq) {
              resp &&= date && date.getTime() < ddub!.getTime();
            }
          }
          if (criteria.type.length > 0) {
            if (!doc.docSubtypeId) return false;
            // at least one of the selected types must match
            resp &&= criteria.type.includes(doc.docSubtypeId);
          }
          return resp;
        }),
        toArray(),
        map((docs) => ({ [trifold]: docs }))
      )
    );

    return forkJoin(obs).pipe(map((results) => results.reduce((acc, curr) => ({ ...acc, ...curr }), {}) as FilteredTrifoldFiles));
  }

  getFormattedNote(note: CommonNote) {
    return (
      note.notes
        ?.replace(/(?:\r\n|\r|\n)/g, '<br>')
        .replace(/<a\s+href="([^"]+)"\s*>([^<]+)<\/a>/gi, '<a href="$1" target="_blank">$2</a>') ?? ''
    );
  }

  /**
   * Build a string representation of the trifold document details.
   * @param doc - The WsFile object representing the document.
   * @returns A string containing the document details.
   */
  getTrifoldDocDetailString(doc: WsFile): string {
    const detail = [];
    if (doc.documentDate) detail.push(doc.documentDate);
    if (doc.docSubtype) detail.push(doc.docSubtype);
    if (/Locked|Checked-Out/i.test(doc.status ?? '')) detail.push(doc.status);
    if (doc.versions && doc.versions.length > 0) {
      detail.push(doc.versions[0].createdBy);
      detail.push(doc.versions[0].versionNumber);
      detail.push(formatDateYYYYMMDD(doc.versions[0].createDate!));
    } else {
      detail.push('Hover for more details');
    }
    return detail.join(' | ');
  }

  /**
   * Retrieves the icon for a trifold document based on the provided record.
   * @param rec - The record containing the file information.
   * @returns The trifold icon name.
   */
  getTrifoldDocIcon(rec: WsFile): string {
    return getTrifoldDocIcon(rec.fileName!, rec.hasAttachments === 'Y');
  }

  /**
   * Returns the icon name based on the file extension.
   * @param rec - The record containing reference file information.
   * @returns The reference document icon.
   */
  getRefDocIcon(rec: CaseReferenceDetails) {
    return getTrifoldDocIcon(rec.fileName!, false);
  }

  /**
   * Retrieves the displayable bibliographic information for a given item.
   * @param item The item to retrieve bibliographic information for.
   * @returns The displayable bibliographic information.
   */
  getDisplayableBiblio(item: CommonIP): DisplayableFieldResponse {
    return this.dataTransformService.displayableCaseDetails(item.ipType, item.detail?.biblio);
  }

  /**
   * Checks if the user has the specified role.
   * @param role The role to check.
   * @returns True if the user has the role, false otherwise.
   */
  hasRole(role: string) {
    return this.accountService.hasRole(role);
  }

  /**
   * Determines the default state of the component based on user settings and item properties.
   */
  determineDefaultState() {
    if (this.selectedTrifoldIndex < 0) {
      const defaultsTrifoldMap = this.settingsService.getDefaultTrifoldsMap();
      const trifold = defaultsTrifoldMap[this.item.ipType];
      const currentIndex = this.trifoldOrder.findIndex((value) => value === trifold);
      this.selectedTrifoldIndex = currentIndex === -1 ? 0 : currentIndex;
    }
    if (this.selectedDetailIndex < 0) {
      const defaultTab = this.settingsService.getDefaultTabByIPType(this.item.ipType);
      const currentIndex = this.detailTabs.findIndex((tab) => tab === defaultTab);
      this.selectedDetailIndex = currentIndex === -1 ? 0 : currentIndex;
    }
    if (this.currentView === undefined) {
      this.currentView = this.settingsService.getDefaultViewByIPType(this.item.ipType);
    }
    // only allow tab animation after the tabs are intially set.
    setTimeout(() => {
      this.tabAnimationDuration = '500ms';
    }, 1000);
  }

  /**
   * Checks if the document is subject to export control.
   * @param doc The document to check.
   * @returns True if the document is subject to export control, false otherwise.
   */
  isExportControlled(doc: WsFile) {
    const isTechDoc = 'technicalDocument' in doc && doc.technicalDocument === 'Y';
    return this.accountService.userSubjectToExportControl && this.item.exportControlStatus === 'Y' && isTechDoc;
  }

  /**
   * Filters the references based on the search term.
   * @param data The array of references to filter.
   * @param searchTerm The search term to apply.
   * @returns The filtered array of references.
   */
  filterReferences(data: CaseReferenceDetails[], searchTerm: string) {
    const searchLower = searchTerm.trim().toLowerCase();
    return data.filter(
      (ref) =>
        searchLower === '' ||
        (ref.titleDisplay ?? '').toLowerCase().includes(searchLower) ||
        (ref.referenceTypeDisplay ?? '').toLowerCase().includes(searchLower)
    );
  }

  /**
   * Filters the workflows based on the search term.
   * @param data The array of workflows to filter.
   * @param searchTerm The search term to apply.
   * @returns The filtered array of workflows.
   */
  filterWorkflows(data: WorkflowDO[], searchTerm: string) {
    const searchLower = searchTerm.trim().toLowerCase();
    return data.filter(
      (wf) =>
        searchLower === '' ||
        (wf.definition?.name ?? '').toLowerCase().includes(searchLower) ||
        (wf.currentTask?.definition?.name ?? '').toLowerCase().includes(searchLower) ||
        (wf.currentTask?.assignedTo ? `${wf.currentTask.assignedTo.firstName} ${wf.currentTask.assignedTo.lastName}` : '')
          .toLowerCase()
          .includes(searchLower)
    );
  }

  /**
   * Filters the notes based on the search term.
   * @param data The array of notes to filter.
   * @param searchTerm The search term to apply.
   * @returns The filtered array of notes.
   */
  filterNotes(data: CommonNote[], searchTerm: string) {
    const searchLower = searchTerm.trim().toLowerCase();
    return data.filter(
      (note) =>
        searchLower === '' ||
        (note.notes ?? '').toLowerCase().includes(searchLower) ||
        (note.createByUser ?? '').toLowerCase().includes(searchLower)
    );
  }

  /**
   * Toggles the selection of a document.
   * @param doc The document to toggle.
   */
  toggleSelection(doc: SelectionDocument) {
    this.selectionModel.toggle(doc);
  }

  /**
   * Tracks the trifold by its index and value.
   * @param index The index of the trifold.
   * @param trifold The trifold value.
   * @returns The trifold value.
   */
  trackByTrifold(index: number, trifold: IP.TrifoldEnum) {
    return trifold;
  }
  trackById = trackByKey<WorkflowDO, number>('id');
  trackByDoc = trackByKey<WsFile | ReferenceDetails | CaseReferenceDetails, number>('fileId');

  /**
   * Gets the filter count.
   *
   * @readonly
   * @type {*}
   */
  get filterCount() {
    const { type, date } = this.filterSubject.value;
    return Number(type.length > 0) + Number(date != null);
  }

  /**
   * Checks if any filter is set.
   *
   * @readonly
   * @type {boolean}
   */
  get isFilterSet() {
    const crit = this.filterSubject.value;
    return crit.search.trim().length > 0 || crit.type.length > 0 || crit.date != null;
  }

  /**
   * Applies the trifold filters from the item state to the active filter subject.
   *
   */
  applyTrifoldFilters() {
    const filters = this.trifoldFilters;
    if (!filters) {
      this.clearTrifoldFilters();
      return;
    }

    const trifoldFilterFormData: TrifoldFilterDialogFormData = {
      ...DEFAULT_TRIFOLD_FILTER_STATE,
      ...(this.filterSubject.value ?? {}),
      type: filters.docSubtypeId ? filters.docSubtypeId : [],
    };

    this.filterSubject.next(trifoldFilterFormData);

    if (filters.docSubtypeId && filters.docSubtypeId.length > 0) {
      this.isFilterVisible = true;
    } else {
      this.isFilterVisible = false;
    }
  }

  /**
   * Gets the upload trifold.
   *
   * @readonly
   * @type {*}
   */
  get uploadTrifold() {
    // current selected trifold might only be read only
    const trifold = this.trifoldOrder[this.selectedTrifoldIndex];
    const uploadableTrifolds = this.accountService.uploadableTrifolds[this.item.ipType];
    // if it also is uploadable use it, otherwise use the first uploadable trifold
    return uploadableTrifolds.includes(trifold) ? trifold : (uploadableTrifolds[0] ?? IP.TrifoldEnum.L);
  }

  /**
   * Checks if a feature is enabled.
   * @param key The feature flag key.
   * @returns Whether the feature is enabled.
   */
  isFeatureEnabled(key: keyof FirstToFileFeatureFlags): boolean {
    return this.accountService.featureFlags[key];
  }

  /**
   * Gets the documents for a specific trifold.
   * @param docsByTrifold The documents organized by trifold.
   * @param trifold The trifold to get documents for.
   * @returns The documents for the specified trifold.
   */
  getDocsForTrifold(docsByTrifold: FilteredTrifoldFiles, trifold: IP.TrifoldEnum) {
    return docsByTrifold[trifold] || [];
  }
}
